import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, Plus, Trash2, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { guardarArchivo } from '@/lib/descargar';

import { SelectorClase } from '../comun/SelectorClase';
import { SelectorEspecialidad } from '../comun/SelectorEspecialidad';

type Temario = components['schemas']['StudyPlan'];
type Tema = components['schemas']['StudyPlanTopic'];
type Resultado = components['schemas']['StudyPlanImportResult'];

const CLAVE = ['contenido', 'temario'];

function useTemario() {
  return useQuery({
    queryKey: CLAVE,
    queryFn: () => unwrap<Temario>(api.GET('/api/v1/admin/study-plan-topics')),
  });
}

function useMutaciones() {
  const qc = useQueryClient();
  const poner = (d: Temario) => {
    qc.setQueryData(CLAVE, d);
    void qc.invalidateQueries({ queryKey: ['contenido', 'clases'] });
  };
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const ruta = (id: number) => ({ params: { path: { id: String(id) } } });
  return {
    crear: useMutation({
      mutationFn: (body: components['schemas']['StudyPlanTopicCreateBody']) =>
        unwrap<Temario>(api.POST('/api/v1/admin/study-plan-topics', { body })),
      onSuccess: poner,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({
        id,
        body,
      }: {
        id: number;
        body: components['schemas']['StudyPlanTopicUpdateBody'];
      }) =>
        unwrap<Temario>(api.PATCH('/api/v1/admin/study-plan-topics/{id}', { ...ruta(id), body })),
      onSuccess: poner,
      onError: err,
    }),
    borrar: useMutation({
      mutationFn: (id: number) =>
        unwrap<Temario>(api.DELETE('/api/v1/admin/study-plan-topics/{id}', ruta(id))),
      onSuccess: poner,
      onError: err,
    }),
    ordenar: useMutation({
      mutationFn: (body: { specialtyId: number | null; ids: number[] }) =>
        unwrap<Temario>(api.PUT('/api/v1/admin/study-plan-topics/order', { body })),
      onSuccess: poner,
      onError: err,
    }),
  };
}

function importar(file: File, dryRun: boolean): Promise<Resultado> {
  return unwrap<Resultado>(
    api.POST('/api/v1/admin/study-plan-topics/import', {
      body: { dryRun: dryRun ? 'true' : 'false', file: file as unknown as string },
      bodySerializer: () => {
        const fd = new FormData();
        fd.set('dryRun', dryRun ? 'true' : 'false');
        fd.set('file', file);
        return fd;
      },
    }),
  );
}

function ModalImportar({ onCerrar }: { onCerrar: () => void }) {
  const qc = useQueryClient();
  const { proteger, dialogo } = useStepUp();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vista, setVista] = useState<Resultado | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const correr = async (dryRun: boolean) => {
    if (!archivo) return;
    setOcupado(true);
    try {
      const r = await proteger(() => importar(archivo, dryRun));
      if (dryRun) setVista(r);
      else {
        toast.success(
          `Temario actualizado: ${r.creates} alta(s), ${r.updates} cambio(s), ${r.deletes} baja(s).`,
        );
        void qc.invalidateQueries({ queryKey: ['contenido'] });
        onCerrar();
      }
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Importar temario (.xlsx)"
      descripcion="Columnas: ID CLASE, ESPECIALIDAD, SUB ESPECIALIDAD, MÓDULO (como la exportación). La clase se busca por ID y, si falta, por nombre. Primero verás los cambios."
      ancho="max-w-2xl"
    >
      <div className="space-y-4">
        <input
          type="file"
          aria-label="Archivo .xlsx"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            setArchivo(e.target.files?.[0] ?? null);
            setVista(null);
          }}
        />
        {vista && (
          <div className="space-y-3 rounded-lg border border-[var(--separador)] p-3 text-sm">
            <p>
              <strong>{vista.rows}</strong> filas leídas · <strong>{vista.creates}</strong> altas ·{' '}
              <strong>{vista.updates}</strong> cambios ({vista.classChanges} de clase) ·{' '}
              <strong className="text-rojo-texto">{vista.deletes}</strong> bajas · {vista.unchanged}{' '}
              sin cambio
            </p>
            {vista.sampleCreates.length > 0 && (
              <p className="text-xs text-texto-2">Altas: {vista.sampleCreates.join(', ')}</p>
            )}
            {vista.sampleDeletes.length > 0 && (
              <p className="text-xs text-rojo-texto">Bajas: {vista.sampleDeletes.join(', ')}</p>
            )}
            {vista.problems.length > 0 && (
              <div>
                <p className="font-bold">Revisa ({vista.problems.length}):</p>
                <ul className="max-h-48 overflow-y-auto text-xs">
                  {vista.problems.map((p, i) => (
                    <li key={i}>
                      Fila {p.row} · {p.module}: {p.issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          {!vista ? (
            <button
              type="button"
              className="btn-cs btn-primario"
              disabled={!archivo || ocupado}
              onClick={() => void correr(true)}
            >
              Ver cambios
            </button>
          ) : (
            <button
              type="button"
              className="btn-cs btn-peligro"
              disabled={ocupado}
              onClick={() => void correr(false)}
            >
              Aplicar cambios
            </button>
          )}
        </div>
      </div>
      {dialogo}
    </Modal>
  );
}

function NuevoTema({ onCerrar }: { onCerrar: () => void }) {
  const { crear } = useMutaciones();
  const [f, setF] = useState({
    module: '',
    specialtyId: null as number | null,
    classId: null as number | null,
  });
  return (
    <Modal abierto onCerrar={onCerrar} titulo="Nuevo tema">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate({ ...f, module: f.module.trim() }, { onSuccess: onCerrar });
        }}
      >
        <Campo etiqueta="Módulo" ayuda="Se guarda en MAYÚSCULAS SIN ACENTOS.">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={150}
            autoFocus
            value={f.module}
            onChange={(e) => setF({ ...f, module: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Especialidad">
          <SelectorEspecialidad
            valor={f.specialtyId}
            onCambio={(specialtyId) => setF({ ...f, specialtyId })}
          />
        </Campo>
        <div>
          <span className="mb-1.5 block text-sm font-bold text-texto-2">Clase</span>
          <SelectorClase valor={f.classId} onCambio={(classId) => setF({ ...f, classId })} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
            Agregar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Fila({ t, asa, editable }: { t: Tema; asa: ReactNode; editable: boolean }) {
  const { editar, borrar } = useMutaciones();
  const [modulo, setModulo] = useState(t.module);
  return (
    <div className="grid grid-cols-[1.5rem_1fr] items-center gap-2 border-b border-[var(--separador)] px-3 py-1.5 text-sm md:grid-cols-[1.5rem_1fr_minmax(16rem,1fr)_auto]">
      <span>{asa}</span>
      <input
        className="input-vidrio py-1"
        aria-label="Módulo"
        value={modulo}
        maxLength={150}
        disabled={!editable}
        onChange={(e) => setModulo(e.target.value)}
        onBlur={() =>
          modulo.trim().length >= 2 &&
          modulo !== t.module &&
          editar.mutate({ id: t.id, body: { module: modulo.trim() } })
        }
      />
      <div className="max-md:col-span-2 max-md:col-start-2">
        <SelectorClase
          valor={t.classId}
          deshabilitado={!editable}
          onCambio={(classId) => editar.mutate({ id: t.id, body: { classId } })}
        />
        {t.classPublished === false && (
          <p className="mt-0.5 text-xs text-amarillo-texto">La clase está oculta</p>
        )}
      </div>
      <div className="flex gap-1 max-md:col-start-2">
        {t.classId && (
          <Link
            to={`/contenido/clases/${t.classId}`}
            className="btn-cs btn-vidrio px-2 py-1"
            aria-label="Abrir la clase"
            title="Abrir la clase"
          >
            <ExternalLink size={14} aria-hidden />
          </Link>
        )}
        {editable && (
          <button
            type="button"
            className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
            aria-label={`Borrar ${t.module}`}
            onClick={() => borrar.mutate(t.id)}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

function Contenido({ d }: { d: Temario }) {
  const editable = useCan('contenido', 'editar');
  const { ordenar } = useMutaciones();
  const { proteger, dialogo } = useStepUp();
  const [filtro, setFiltro] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [importando, setImportando] = useState(false);
  const f = filtro.trim().toUpperCase();
  const grupos = new Map<string, { specialtyId: number | null; temas: Tema[] }>();
  d.topics.forEach((t) => {
    const k = t.specialtyName ?? 'Sin especialidad';
    const g = grupos.get(k) ?? { specialtyId: t.specialtyId, temas: [] };
    g.temas.push(t);
    grupos.set(k, g);
  });
  const exportar = async () => {
    try {
      await proteger(async () =>
        guardarArchivo(
          await api.GET('/api/v1/admin/study-plan-topics/export', { parseAs: 'blob' }),
          'temario.xlsx',
        ),
      );
    } catch (e) {
      toast.error(mensajeDe(e));
    }
  };
  return (
    <>
      <Encabezado
        titulo="Temario"
        descripcion={`${d.topics.length} temas. El alumno ve una clase solo si un tema apunta a ella. Arrastra para ordenar dentro de cada especialidad.`}
        acciones={
          <>
            <button type="button" className="btn-cs btn-vidrio" onClick={() => void exportar()}>
              <Download size={16} aria-hidden /> Exportar .xlsx
            </button>
            {editable && (
              <>
                <button
                  type="button"
                  className="btn-cs btn-vidrio"
                  onClick={() => setImportando(true)}
                >
                  <Upload size={16} aria-hidden /> Importar
                </button>
                <button
                  type="button"
                  className="btn-cs btn-primario"
                  onClick={() => setNuevo(true)}
                >
                  <Plus size={16} aria-hidden /> Nuevo tema
                </button>
              </>
            )}
          </>
        }
      />
      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_2fr]">
        <Campo etiqueta="Filtrar">
          <input
            className="input-vidrio"
            placeholder="Módulo o clase"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </Campo>
        <div className="panel-vidrio py-3 text-sm">
          <p>
            <strong>{d.withoutClass}</strong> tema(s) sin clase ·{' '}
            <strong>{d.publishedClassesWithoutTopic.length}</strong> clase(s) visibles sin tema
          </p>
          {d.publishedClassesWithoutTopic.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-texto-3">Ver clases sin tema</summary>
              <ul className="mt-1 max-h-40 overflow-y-auto text-xs">
                {d.publishedClassesWithoutTopic.map((c) => (
                  <li key={c.id}>
                    <Link to={`/contenido/clases/${c.id}`} className="hover:underline">
                      {c.name}
                    </Link>{' '}
                    <span className="text-texto-3">{c.specialtyName ?? ''}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
      {d.topics.length === 0 && <Vacio titulo="El temario está vacío" />}
      <div className="space-y-5">
        {[...grupos].map(([nombre, g]) => {
          const temas = f
            ? g.temas.filter(
                (t) => t.module.includes(f) || (t.className ?? '').toUpperCase().includes(f),
              )
            : g.temas;
          if (temas.length === 0) return null;
          return (
            <section key={nombre} className="panel-vidrio p-0">
              <h2 className="border-b border-[var(--separador)] px-3 py-2 font-extrabold">
                {nombre}{' '}
                <span className="text-sm font-normal text-texto-3">({g.temas.length})</span>
              </h2>
              <OrdenableLista
                items={temas}
                idDe={(t) => t.id}
                deshabilitado={!editable || f !== '' || ordenar.isPending}
                onOrdenar={(ids) => ordenar.mutate({ specialtyId: g.specialtyId, ids })}
              >
                {(t, asa) => (
                  <Fila key={`${t.id}-${t.module}`} t={t} asa={asa} editable={editable} />
                )}
              </OrdenableLista>
            </section>
          );
        })}
      </div>
      {nuevo && <NuevoTema onCerrar={() => setNuevo(false)} />}
      {importando && <ModalImportar onCerrar={() => setImportando(false)} />}
      {dialogo}
    </>
  );
}

export function TemarioPage() {
  const q = useTemario();
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Contenido d={q.data} />}
    </PermissionGate>
  );
}
